import { number, schema, string } from "@anna-money/anna-web-lib/serializer";
import * as Sentry from "@sentry/browser";
import { type Path } from "react-router-dom";
import { type DataStorage } from "services/storage/dataStorage";

const sessionKeyPrefix = "auth_session_";
const sessionLifetimeMs = 60 * 60 * 1000;
const sessionIdBytes = 16;

export class AuthSession extends schema(
  {
    pathname: string(),
    search: string(),
    hash: string(),
    createdAt: number(),
  },
  "AuthSession",
) {}

const getSessionKey = (sessionId: string): string => `${sessionKeyPrefix}${sessionId}`;

const createSessionId = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(sessionIdBytes)), (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );

/**
 * Keeps the destination a login should return to. One key per login attempt, so parallel tabs
 * don't overwrite each other, and the id doubles as the OAuth `state` parameter.
 */
export class AuthSessionStorage {
  private readonly _dataStorage: DataStorage;

  constructor(dataStorage: DataStorage) {
    this._dataStorage = dataStorage;
  }

  start(entryPath: Path): string {
    this.cleanup();

    const sessionId = createSessionId();
    this._dataStorage.set(
      getSessionKey(sessionId),
      new AuthSession({
        pathname: entryPath.pathname,
        search: entryPath.search,
        hash: entryPath.hash,
        createdAt: Date.now(),
      }),
    );

    return sessionId;
  }

  load(sessionId: string): AuthSession | null {
    const key = getSessionKey(sessionId);

    let session: AuthSession | null;
    try {
      session = this._dataStorage.get(key, AuthSession);
    } catch (e) {
      Sentry.captureException(e, { extra: { sessionId, reason: "auth session failed to deserialise" } });
      this._dataStorage.remove(key);
      return null;
    }

    if (session && Date.now() - session.createdAt >= sessionLifetimeMs) {
      this._dataStorage.remove(key);
      return null;
    }

    return session;
  }

  remove(sessionId: string): void {
    this._dataStorage.remove(getSessionKey(sessionId));
  }

  cleanup(): void {
    for (const key of this._dataStorage.getKeys()) {
      if (key.startsWith(sessionKeyPrefix)) {
        // Reading a session is what drops it when it is expired or unreadable.
        this.load(key.substring(sessionKeyPrefix.length));
      }
    }
  }
}
